/**
 * RiskAuditLog — page version of the simple_history audit drawer.
 *
 * Renders the chronological list of changes on a `Risk` and any related
 * `RiskSnapshot` rows, computing the field-level diff between adjacent
 * versions on the client so the table only shows what actually changed at
 * each step.
 *
 * Backend: `riskHistoryUrl(riskId)` returns an array of `RiskHistoryEntry`
 * objects sorted oldest-first (we sort them newest-first here for display).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/Page Component/PageTitle";
import { fetchData } from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { riskDetailUrl, riskHistoryUrl } from "../../../Service/Apis";
import { Risk, RiskHistoryEntry } from "./Types/RiskManagementTypes";
import { normalizeRisk, normalizeRiskHistoryList } from "./components/normalizers";

const HUMAN_FIELD: Record<string, string> = {
  impact: "Impact",
  probability: "Probability",
  exposure: "Exposure",
  level: "Level",
  mitigation_plan: "Mitigation plan",
  target_date: "Target date",
  status: "Status",
  comments: "Comments",
  description: "Description",
  risk_owner: "Risk owner",
  contributor: "Contributor",
  area: "Area",
  is_closed: "Closed",
  closed_reason: "Closed reason",
};

const fieldLabel = (field: string): string => HUMAN_FIELD[field] ?? field;

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const RiskAuditLog: React.FC = () => {
  const { riskId } = useParams<{ riskId: string }>();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const showToast = useCustomToast();

  const [risk, setRisk] = useState<Risk | null>(null);
  const [entries, setEntries] = useState<RiskHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!riskId) return;
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        dispatch(fetchData(riskDetailUrl(riskId))),
        dispatch(fetchData(riskHistoryUrl(riskId))),
      ]);
      setRisk(normalizeRisk(r));
      setEntries(normalizeRiskHistoryList(h));
    } catch {
      showToast("Failed to load audit log.", "error", false);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskId, dispatch]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /**
   * Backend gives us a `changes` map already (mapped from its `diff` list by
   * `normalizeRiskHistoryEntry`). As a defensive fallback, when an entry's
   * `changes` map is empty *and* it isn't a CREATE row, diff it against the
   * previous entry for the same (model, object id) pair on the client side.
   *
   * For CREATE rows we surface the initial values from the `data` snapshot so
   * the audit log isn't useless on the first row.
   */
  const enrichedEntries = useMemo(() => {
    const sorted = [...entries].sort(
      (a, b) =>
        new Date(a.history_date).getTime() - new Date(b.history_date).getTime()
    );
    const prevByKey: Record<string, RiskHistoryEntry> = {};
    const interestingKeys = [
      "description",
      "impact",
      "probability",
      "exposure",
      "level",
      "mitigation_plan",
      "target_date",
      "status",
      "comments",
      "is_closed",
      "closed_reason",
    ];
    sorted.forEach((entry) => {
      const objKey = `${entry.model}:${entry.object_id ?? ""}`;
      const isCreate = entry.history_type === "+";
      const isEmpty = !entry.changes || Object.keys(entry.changes).length === 0;

      if (isEmpty && isCreate) {
        // Show the snapshot of initial values as "—" → value.
        const initial: Record<string, [unknown, unknown]> = {};
        interestingKeys.forEach((k) => {
          const v = (entry.data ?? {})[k];
          if (v !== undefined && v !== null && v !== "") {
            initial[k] = [null, v];
          }
        });
        entry.changes = initial;
      } else if (isEmpty) {
        const prev = prevByKey[objKey];
        if (prev) {
          const diff: Record<string, [unknown, unknown]> = {};
          interestingKeys.forEach((k) => {
            const oldV = prev.data?.[k];
            const newV = entry.data?.[k];
            if (JSON.stringify(oldV) !== JSON.stringify(newV)) {
              diff[k] = [oldV ?? null, newV ?? null];
            }
          });
          entry.changes = diff;
        }
      }
      prevByKey[objKey] = entry;
    });
    return sorted.reverse();
  }, [entries]);

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 12, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!risk) {
    return (
      <Container maxWidth="md" sx={{ mt: 12 }}>
        <Alert severity="error">Risk not found.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 10, mb: 6 }}>
      <PageTitle>Risk Audit Log</PageTitle>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Risk
            </Typography>
            <Typography variant="h6">{risk.description}</Typography>
            <Typography variant="body2" color="text.secondary">
              Area: {risk.area_name ?? "—"} • Contributor:{" "}
              {risk.contributor_name ?? "—"}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<TimelineIcon />}
            onClick={() =>
              navigate(`/pms/risk-management/risk/${risk.id}/history`)
            }
          >
            Trend view
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Object</TableCell>
              <TableCell>Operation</TableCell>
              <TableCell>Changes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enrichedEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" align="center">
                    No history recorded yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              enrichedEntries.map((entry) => {
                const changes = entry.changes ?? {};
                const changeKeys = Object.keys(changes);
                return (
                  <TableRow key={`${entry.model}-${entry.history_id}`}>
                    <TableCell sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {new Date(entry.history_date).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      {entry.history_user_name ?? entry.history_user ?? "system"}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Chip size="small" label={entry.model} />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Chip
                        size="small"
                        label={
                          entry.history_type === "+"
                            ? "CREATE"
                            : entry.history_type === "-"
                            ? "DELETE"
                            : "UPDATE"
                        }
                        color={
                          entry.history_type === "+"
                            ? "success"
                            : entry.history_type === "-"
                            ? "error"
                            : "info"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {changeKeys.length === 0 ? (
                        <Typography variant="caption" color="text.disabled">
                          (no field-level changes)
                        </Typography>
                      ) : (
                        <Stack spacing={0.5}>
                          {changeKeys.map((field) => {
                            const [oldVal, newVal] = changes[field];
                            return (
                              <Box key={field} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                <Chip
                                  size="small"
                                  label={fieldLabel(field)}
                                  variant="outlined"
                                />
                                <Typography variant="body2" color="error.main">
                                  {formatValue(oldVal)}
                                </Typography>
                                <Typography variant="body2">→</Typography>
                                <Typography variant="body2" color="success.main">
                                  {formatValue(newVal)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default RiskAuditLog;
